import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Coins, Camera } from "lucide-react";
import { DAYS_PT, formatBRL } from "@/lib/api";
import { toast } from "sonner";

function TaskForm({ open, onOpenChange, task, defaultChildId, children, onDone }) {
    const [childId, setChildId] = useState(defaultChildId);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [reward, setReward] = useState("0");
    const [days, setDays] = useState([]);
    const [photoRequired, setPhotoRequired] = useState(false);

    useEffect(() => {
        if (open) {
            setChildId(task?.child_id || defaultChildId);
            setTitle(task?.title || "");
            setDesc(task?.description || "");
            setReward(String(task?.reward_amount ?? 0));
            setDays(task?.recurring_days || []);
            setPhotoRequired(!!task?.photo_required);
        }
    }, [open, task, defaultChildId]);

    const save = async () => {
        if (!title.trim() || !childId) return;
        const payload = {
            child_id: childId,
            title: title.trim(),
            description: desc.trim(),
            recurring_days: days,
            reward_amount: parseFloat((reward || "0").replace(",", ".")) || 0,
            photo_required: photoRequired,
            active: true,
        };
        try {
            if (task?.id) await api.put(`/tasks/${task.id}`, payload);
            else await api.post("/tasks", payload);
            toast.success("Tarefa salva");
            onDone?.();
            onOpenChange(false);
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="task-dialog">
                <DialogHeader><DialogTitle>{task?.id ? "Editar tarefa" : "Nova tarefa"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    {children?.length > 1 && (
                        <div>
                            <Label>Para qual filho?</Label>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {children.map((c) => {
                                    const active = c.id === childId;
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => setChildId(c.id)}
                                            data-testid={`task-child-${c.id}`}
                                            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${active ? "border-[#D96B43] bg-[#FFF1EA] font-semibold" : "border-[#E2DCD5] bg-white"}`}
                                        >
                                            <span className="h-5 w-5 rounded-full text-white text-[11px] grid place-items-center" style={{ backgroundColor: c.avatar_color }}>
                                                {c.name?.[0]?.toUpperCase()}
                                            </span>
                                            {c.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div>
                        <Label>Título</Label>
                        <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Arrumar a cama" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Descrição (opcional)</Label>
                        <Textarea data-testid="task-description-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Recompensa (R$) — deixe 0 se não é tarefa paga</Label>
                        <Input data-testid="task-reward-input" type="number" step="0.01" min="0" value={reward} onChange={(e) => setReward(e.target.value)} className="mt-1.5" />
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-[#F3EFEA] p-3">
                        <div className="flex items-center gap-2.5">
                            <Camera className="h-5 w-5 text-[#D96B43]" />
                            <div>
                                <div className="font-semibold text-sm">Foto obrigatória</div>
                                <div className="text-xs text-slate-500">A criança precisa anexar foto para concluir</div>
                            </div>
                        </div>
                        <Switch
                            checked={photoRequired}
                            onCheckedChange={setPhotoRequired}
                            data-testid="task-photo-required-switch"
                        />
                    </div>
                    <div>
                        <Label>Dias da semana (vazio = todos os dias)</Label>
                        <div className="grid grid-cols-7 gap-1 mt-2">
                            {DAYS_PT.map((d, i) => {
                                const active = days.includes(i);
                                return (
                                    <button
                                        key={i}
                                        onClick={() => setDays((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].sort())}
                                        data-testid={`task-day-${i}`}
                                        className={`rounded-lg py-2 text-xs font-semibold ${active ? "bg-[#D96B43] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                    >{d}</button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="task-cancel">Cancelar</Button>
                    <Button onClick={save} className="bg-[#D96B43] hover:bg-[#C25933] text-white rounded-full" data-testid="task-save-button">Salvar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function TaskList() {
    const { activeChildId, children } = useApp();
    const { isChild } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all"); // "all" | child id

    const load = async () => {
        const scope = isChild ? activeChildId : (filter === "all" ? null : filter);
        const params = scope ? { child_id: scope } : {};
        const { data } = await api.get("/tasks", { params });
        setTasks(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeChildId, filter, isChild]);

    const remove = async (t) => {
        if (!window.confirm(`Remover tarefa "${t.title}"?`)) return;
        await api.delete(`/tasks/${t.id}`);
        toast.success("Removida");
        load();
    };

    if (!children.length && !isChild) {
        return <EmptyState message="Adicione um filho primeiro para criar tarefas." />;
    }

    const childrenMap = Object.fromEntries(children.map((c) => [c.id, c]));

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap rise">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#8a7968]">Rotina</div>
                    <h1 className="text-3xl sm:text-4xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>
                        {isChild ? "Minhas tarefas" : "Tarefas da família"}
                    </h1>
                </div>
                {!isChild && (
                    <Button
                        onClick={() => { setEditing(null); setOpen(true); }}
                        data-testid="new-task-button"
                        className="bg-[#D96B43] hover:bg-[#C25933] text-white rounded-full"
                    >
                        <Plus className="h-4 w-4 mr-1.5" /> Nova tarefa
                    </Button>
                )}
            </div>

            {!isChild && children.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setFilter("all")}
                        data-testid="filter-all"
                        className={`rounded-full border px-3 py-1.5 text-sm ${filter === "all" ? "border-[#D96B43] bg-[#FFF1EA] font-semibold" : "border-[#E2DCD5] bg-white"}`}
                    >Todos</button>
                    {children.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setFilter(c.id)}
                            data-testid={`filter-${c.id}`}
                            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${filter === c.id ? "border-[#D96B43] bg-[#FFF1EA] font-semibold" : "border-[#E2DCD5] bg-white"}`}
                        >
                            <span className="h-5 w-5 rounded-full text-white text-[11px] grid place-items-center" style={{ backgroundColor: c.avatar_color }}>{c.name?.[0]?.toUpperCase()}</span>
                            {c.name}
                        </button>
                    ))}
                </div>
            )}

            {tasks.length === 0 ? (
                <EmptyState message="Nenhuma tarefa ainda. Que tal começar com 'Arrumar a cama'?" />
            ) : (
                <div className="grid md:grid-cols-2 gap-4 rise rise-1">
                    {tasks.map((t) => {
                        const c = childrenMap[t.child_id];
                        return (
                            <Card key={t.id} className="p-5 border-[#E2DCD5]" data-testid={`task-card-${t.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            {c && !isChild && (
                                                <span
                                                    className="chip border"
                                                    style={{ backgroundColor: `${c.avatar_color}22`, color: c.avatar_color, borderColor: `${c.avatar_color}44` }}
                                                >
                                                    {c.name}
                                                </span>
                                            )}
                                            {t.photo_required && (
                                                <span className="chip bg-[#FFF1EA] text-[#C25933] border border-[#F2CBB6]">
                                                    <Camera className="h-3 w-3" /> foto
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-semibold text-slate-900 truncate">{t.title}</div>
                                        {t.description && <div className="text-sm text-slate-500 mt-0.5">{t.description}</div>}
                                        <div className="flex flex-wrap gap-1 mt-3">
                                            {(t.recurring_days?.length ? t.recurring_days : [0, 1, 2, 3, 4, 5, 6]).map((d) => (
                                                <span key={d} className="chip bg-slate-100 text-slate-600">{DAYS_PT[d]}</span>
                                            ))}
                                        </div>
                                        {t.reward_amount > 0 && (
                                            <div className="mt-3 chip bg-amber-50 text-amber-800 border border-amber-200">
                                                <Coins className="h-3 w-3" /> {formatBRL(t.reward_amount)}
                                            </div>
                                        )}
                                    </div>
                                    {!isChild && (
                                        <div className="flex flex-col gap-1">
                                            <Button variant="ghost" size="icon" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`edit-task-${t.id}`}>
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`delete-task-${t.id}`}>
                                                <Trash2 className="h-4 w-4 text-red-600" />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            <TaskForm
                open={open}
                onOpenChange={setOpen}
                task={editing}
                defaultChildId={filter !== "all" ? filter : children[0]?.id}
                children={children}
                onDone={load}
            />
        </div>
    );
}

function EmptyState({ message }) {
    return (
        <Card className="p-10 border-[#E2DCD5] text-center">
            <p className="text-slate-600">{message}</p>
        </Card>
    );
}
